"use client";

import {
  WorkplaceRequestElement,
  WorkplaceRequestLocationValue
} from "@gettactic/api";
import { useFloorsForOffices, useResource } from "@gettactic/hooks";
import React from "react";
import { LocationOnMap } from "@/features/workplace-requests/components/detail-modal/LocationOnMap";

interface LocationOnMapPanelProps {
  request: WorkplaceRequestElement;
  /** Office display name for the footer label, resolved by the parent. */
  officeName: string;
  /**
   * Resource to show instead of the one the request carries — set when the reservation
   * behind the request holds several rooms and the user picked one of them.
   */
  focusResourceId?: string | null;
}

const getLocationValue = (
  request: WorkplaceRequestElement
): WorkplaceRequestLocationValue | undefined => request.location ?? undefined;

/**
 * Synchronously resolves the map location a request carries, without the async
 * resource/floor lookups the panel needs to render. Shared so callers can decide
 * whether the panel will render at all (e.g. to avoid a dangling separator above
 * the next section) using the same rules as the panel itself.
 */
export const getRequestMapLocation = (request: WorkplaceRequestElement) => {
  const locationValue = getLocationValue(request);
  const resourceId =
    locationValue?.type === "resource"
      ? locationValue.resource_id
      : request.resource_id;
  const point = locationValue?.type === "coordinates" ? locationValue : null;
  const floorOnlyId =
    locationValue?.type === "floor" ? locationValue.floor_id : null;
  return {
    resourceId,
    point,
    floorOnlyId,
    hasLocation: !!resourceId || !!point || !!floorOnlyId
  };
};

/**
 * "Location on map" preview shown above the request details. Renders an embedded,
 * read-only floor map for whichever ticket-level `location` the request carries
 * (falling back to the legacy top-level `resource_id`):
 * - a resource (`location.type === "resource"`) → the resource is highlighted on its floor;
 * - a pinned point (`location.type === "coordinates"`) → a static pin at x/y;
 * - a floor (`location.type === "floor"`) → just the floor.
 *
 * Renders nothing when the ticket has no location to show. The pin only ever has
 * data for ticket types that define a `location` field, since that is the only
 * place pin coordinates are persisted.
 */
export const LocationOnMapPanel: React.FC<LocationOnMapPanelProps> = ({
  request,
  officeName,
  focusResourceId
}) => {
  const {
    resourceId: requestResourceId,
    point: requestPoint,
    floorOnlyId,
    hasLocation: requestHasLocation
  } = getRequestMapLocation(request);

  // A picked room wins over whatever the ticket itself points at — including a
  // pinned point, which belongs to the ticket rather than to the room.
  const resourceId = focusResourceId ?? requestResourceId;
  const point = focusResourceId ? null : requestPoint;
  const hasLocation = requestHasLocation || !!focusResourceId;

  // A resource carries its own office/floor (its `parent_id`); the point/floor
  // variants are anchored to the request's office.
  const { resource } = useResource(resourceId ?? null);
  const officeId = resource?.office_id ?? request.office_id;
  // A picked room brings its own floor: the ticket's floor must not pin the map to
  // another one when the rooms are spread over several floors.
  const floorId = focusResourceId
    ? (resource?.parent_id ?? null)
    : (point?.floor_id ?? floorOnlyId ?? resource?.parent_id ?? null);

  const floorsQuery = useFloorsForOffices(officeId ? [officeId] : []);
  const floor = floorsQuery.data?.find((f) => f.id === floorId);
  const floorName = floor?.label || floor?.name;

  if (!hasLocation) return null;

  return (
    // The card carries no spacing of its own. Whatever leads the request —
    // the approval box, the description, or just the header chips — the map
    // starts the same distance below it, like the sibling sections do.
    <section className="mt-6">
      <LocationOnMap
        officeId={officeId}
        floorId={floorId}
        resourceId={resourceId}
        point={point}
        officeName={officeName}
        floorName={floorName}
      />
    </section>
  );
};
